import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { HeroComponent } from "../atoms/homeContent";
import { CustomSection } from "../molecules/Section";
import { ImpactConnectionsSection } from "../atoms/ConnectionsSection";
import Portfolio from "../atoms/Portfolio";
import { EffectiveSolutionsSection } from "../atoms/SolutionsSection";
import { Footer } from "../atoms/Footer";
import { HomeProps } from "../../types";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

export const Home: React.FC<HomeProps> = ({ scrollToPortafolio }) => {
  const { useSectionInView, scrollToSection } = useScrollAnimation();
  const { section } = useParams<{ section?: string }>();
  const location = useLocation();

  const section2Ref = useRef<HTMLDivElement>(null);
  const section3Ref = useRef<HTMLDivElement>(null);
  const section4Ref = useRef<HTMLDivElement>(null);

  // Use the scroll animation hook for each section
  const section1 = useSectionInView("section1");
  const section2 = useSectionInView("section2");
  const section3 = useSectionInView("section3");
  const section4 = useSectionInView("section4");
  const section5 = useSectionInView("section5");

  function scrollTo(section: React.RefObject<HTMLDivElement>) {
    if (section.current) {
      section.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }

  useEffect(() => {
    scrollToPortafolio(section4Ref, scrollTo);
  }, []);

  // Scroll al portafolio solo una vez por navegación (location.key nuevo);
  // sin este guard, el efecto se re-dispara en cada re-render mientras la URL
  // siga en /home/portafolio y devuelve al usuario a la sección al intentar salir.
  const lastPortafolioKey = useRef<string | null>(null);
  const [portfolioResetKey, setPortfolioResetKey] = useState<string | null>(
    null
  );
  useEffect(() => {
    if (
      section === "portafolio" &&
      location.key !== lastPortafolioKey.current
    ) {
      lastPortafolioKey.current = location.key;
      setPortfolioResetKey(location.key);
      setTimeout(() => {
        scrollToSection("section4");
      }, 300);
    }
  }, [section, location.key, scrollToSection]);

  // Scroll al inicio cuando se navega desde el logo
  const lastLocationKey = useRef<string | null>(null);
  useEffect(() => {
    // Solo hacer scroll si viene del logo (tiene scrollToTop) y es una navegación nueva
    if (
      location.state?.scrollToTop &&
      location.key !== lastLocationKey.current
    ) {
      lastLocationKey.current = location.key;
      setTimeout(() => {
        scrollToSection("section1");
        // Limpiar el state inmediatamente después de usarlo
        window.history.replaceState({}, document.title);
      }, 100);
    }
  }, [location.key, location.state, scrollToSection]);

  return (
    <div className="App">
      <div className="h-screen w-screen overflow-y-auto snap-y snap-mandatory scroll-smooth">
        <div ref={section1.ref} className="snap-start snap-always">
          <CustomSection
            headline={
              <HeroComponent
                scrollTo={() => scrollToSection("section2")}
                goToSelectionRef={section2Ref}
                goToPortafolioRef={section4Ref}
              />
            }
            goToSectionRef={section2Ref}
            goToPortafolioRef={section4Ref}
            scrollTo={scrollTo}
            showArrow={true}
            sectionId="section1"
          />
        </div>

        <div ref={section2.ref} className="snap-start snap-always">
          <CustomSection
            headline={<ImpactConnectionsSection />}
            goToSectionRef={section3Ref}
            scrollTo={scrollTo}
            showArrow={true}
            sectionId="section2"
          />
        </div>

        <div ref={section3.ref} className="snap-start snap-always">
          <CustomSection
            headline={<EffectiveSolutionsSection />}
            goToSectionRef={section4Ref}
            scrollTo={scrollTo}
            showArrow={true}
            sectionId="section3"
          />
        </div>

        <div ref={section4.ref} className="snap-start snap-always">
          <Portfolio
            onExitPrev={() => scrollToSection("section3")}
            onExitNext={() => scrollToSection("section5")}
            resetKey={portfolioResetKey}
          />
        </div>

        <div ref={section5.ref} className="snap-start snap-always">
          <CustomSection
            headline={
              <Footer scrollTo={scrollTo} goToSelectionRef={section4Ref} />
            }
            scrollTo={scrollTo}
            showArrow={true}
            sectionId="section5"
            isFooter={true}
          />
        </div>
      </div>
    </div>
  );
};
export default Home;
